// LevelsModal — customize the 4 org tiers (name · icon · optional SQL column).
// Edits a local draft; nothing persists until "Save levels", when the parent
// bulk-updates via the API. Reset repopulates the draft from the frontend
// defaults (ORG_LEVELS) — it does not touch the server until saved.
// Cancel / X / Escape discard.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, IconButton, Input, Switch } from '@/components/common';
import Modal from '@/components/common/Modal';
import { getLevel } from '@/mocks/structure';
import type { Level } from '@/api/endpoints/levelsApi';
import LevelIconPicker from './LevelIconPicker';

/** A draft row mirrors a Level; `id` is retained so save maps back to the entity. */
export type LevelDraft = {
  id: string;
  level: number;
  name: string;
  icon: string;
  sqlColumn: string;
  sqlColumnEnabled: boolean;
};

interface LevelsModalProps {
  open: boolean;
  levels: Level[];
  saving?: boolean;
  onClose: () => void;
  onSave: (draft: LevelDraft[]) => void;
}

const LevelsModal = ({ open, levels, saving = false, onClose, onSave }: LevelsModalProps) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<LevelDraft[]>([]);

  // Re-seed the draft each time the modal opens; discard leftovers on close.
  useEffect(() => {
    if (open) {
      setDraft(
        [...levels]
          .sort((a, b) => a.level - b.level)
          .map((l) => ({
            id: l.id,
            level: l.level,
            name: l.name,
            icon: l.icon,
            sqlColumn: l.sqlColumn,
            sqlColumnEnabled: l.sqlColumnEnabled,
          })),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const patch = (index: number, changes: Partial<LevelDraft>) =>
    setDraft((prev) => prev.map((row, i) => (i === index ? { ...row, ...changes } : row)));

  // Reset restores the frontend defaults (name + icon) and clears any SQL column.
  const reset = () =>
    setDraft((prev) =>
      prev.map((row) => {
        const def = getLevel(row.level);
        return { ...row, name: def.label, icon: def.icon };
      }),
    );

  const save = () =>
    onSave(
      draft.map((row) => {
        const sqlColumn = row.sqlColumn.trim();
        return {
          ...row,
          name: row.name.trim(),
          sqlColumn,
          sqlColumnEnabled: sqlColumn ? row.sqlColumnEnabled : false,
        };
      }),
    );

  return (
    <Modal
      open={open}
      onClose={onClose}
      size='lg'
      icon='sliders-horizontal'
      title={t('structure_action_levels')}
      footer={
        <>
          <Button
            variant='ghost'
            iconStart='rotate-ccw'
            className='levels-modal__reset'
            onClick={reset}
            disabled={saving}
          >
            {t('levels_reset')}
          </Button>
          <Button variant='secondary' onClick={onClose} disabled={saving}>
            {t('button_cancel')}
          </Button>
          <Button iconStart='check' onClick={save} loading={saving}>
            {t('levels_save')}
          </Button>
        </>
      }
    >
      <div className='levels-modal'>
        <div className='levels-modal__head' aria-hidden='true'>
          <span>{t('levels_col_icon')}</span>
          <span>{t('levels_col_level')}</span>
          <span>{t('levels_col_name')}</span>
          <span>{t('levels_col_sql')}</span>
          <span />
        </div>

        {draft.map((row, index) => {
          const tone = getLevel(row.level).tone;
          const hasSql = !!row.sqlColumn.trim();
          return (
            <div key={row.id} className='levels-modal__row'>
              <LevelIconPicker
                value={row.icon}
                tone={tone}
                onChange={(icon) => patch(index, { icon })}
              />

              <span className='levels-modal__level'>{t('structure_level', { n: row.level })}</span>

              <Input
                size='sm'
                value={row.name}
                onChange={(e) => patch(index, { name: e.target.value })}
              />

              <div className='levels-modal__sql'>
                <Input
                  size='sm'
                  iconStart='database'
                  placeholder={t('levels_sql_placeholder')}
                  value={row.sqlColumn}
                  disabled={row.sqlColumnEnabled}
                  onChange={(e) => patch(index, { sqlColumn: e.target.value })}
                />
                {hasSql && (
                  <Switch
                    aria-label={t('levels_toggle_sql')}
                    checked={row.sqlColumnEnabled}
                    onChange={(e) => patch(index, { sqlColumnEnabled: e.target.checked })}
                  />
                )}
              </div>

              <IconButton
                icon='trash-2'
                variant='ghost'
                className='levels-modal__delete'
                aria-label={t('levels_clear_sql')}
                disabled={!hasSql || row.sqlColumnEnabled}
                onClick={() => patch(index, { sqlColumn: '', sqlColumnEnabled: false })}
              />
            </div>
          );
        })}

        <p className='levels-modal__note'>
          <Icon name='info' size={16} />
          {t('levels_note')}
        </p>
      </div>
    </Modal>
  );
};

export default LevelsModal;
